import { useCallback } from 'react';
import { CONFIG, LogLevel } from '@frameio/config';
import { reportError } from '@frameio/telemetry';
import { log } from '@frameio/utils';

/**
 * A hook to report error details (Datadog) within an error handler
 *
 * @returns {Function} an onError function that tracks the error passed in and
 * then executes the original onError callback if one was provided.
 */
export function useOnErrorWithTracking<T extends Error>(
  /** an onError function for a React ErrorBoundary */
  onError?: (error: T, ...args: Array<any>) => void,
  isErrorBoundary?: boolean
) {
  return useCallback(
    (error: T) => {
      // Don't handle Next.js errors, needed for 404s, redirects, etc.
      if (error.message.startsWith('NEXT_')) throw error;

      if (CONFIG.logLevel >= LogLevel.DEBUG) {
        // eslint-disable-next-line no-console
        console.error(error);
      }

      log.error('Error boundary caught an error', error);

      // Log visible (P0) error states to DataDog for SLO tracking

      // If an error boundary is reporting an error it's likely
      // an unexpected error visible to the user and impacting UX
      if (isErrorBoundary) {
        reportError(error, { message: 'Error State Rendered' });
      }

      onError?.(error);
    },
    [onError, isErrorBoundary]
  );
}
